import type { Schemas } from '@npc/api-client'

import { api } from './api'
import { call } from './errors'

export type RemoteAISettings = Schemas['RemoteAISettings']
export type RemoteAIInvitationCreate = Schemas['RemoteAIInvitationCreate']
export type RemoteAIInvitationBundle = Schemas['RemoteAIInvitationBundle']
export type RemoteAIInvitationRedeemBundle = Schemas['RemoteAIInvitationRedeemBundle']
export type RemoteAIStatus = Schemas['RemoteAIStatusUpdate']['status']

export function getRemoteAISettings(projectId: string): Promise<RemoteAISettings> {
  return call('/projects/{projectId}/remote-ai', () =>
    api.GET('/projects/{projectId}/remote-ai', { params: { path: { projectId } } }),
  )
}

export function createRemoteAIInvitation(
  projectId: string,
  input: RemoteAIInvitationCreate,
): Promise<RemoteAIInvitationBundle> {
  return call('/projects/{projectId}/remote-ai/invitations', () =>
    api.POST('/projects/{projectId}/remote-ai/invitations', {
      params: { path: { projectId } },
      body: input,
    }),
  )
}

export function createLocalAISetup(): Promise<RemoteAIInvitationBundle> {
  return call('/local-ai/setup', () => api.POST('/local-ai/setup'))
}

/**
 * 作废一张还没被领走的邀请。
 *
 * 与 `revokeRemoteAI` 分成两条，因为撤的不是同一样东西：这条撤的是一把
 * 还没兑换的 bearer secret，那条撤的是一个已经在干活的身份（连着凭证、
 * 沙箱与已签发的发布能力）。后端也拒绝拿这条去撤已兑换的那张。
 *
 * 服务端幂等：重复撤同一张回 204，不回错。
 */
export async function revokeRemoteAIInvitation(projectId: string, invitationId: string): Promise<void> {
  await call('/projects/{projectId}/remote-ai/invitations/{invitationId}', () =>
    api.DELETE('/projects/{projectId}/remote-ai/invitations/{invitationId}', {
      params: { path: { projectId, invitationId } },
    }),
  )
}

export function redeemRemoteAIInvitation(inviteToken: string): Promise<RemoteAIInvitationRedeemBundle> {
  return call('/public/remote-ai/invitations/redeem', () =>
    api.POST('/public/remote-ai/invitations/redeem', { body: { invite_token: inviteToken } }),
  )
}

export async function updateRemoteAIStatus(
  projectId: string,
  identityId: string,
  status: RemoteAIStatus,
): Promise<void> {
  await call('/projects/{projectId}/remote-ai/{identityId}', () =>
    api.PATCH('/projects/{projectId}/remote-ai/{identityId}', {
      params: { path: { projectId, identityId } },
      body: { status },
    }),
  )
}

export async function revokeRemoteAI(projectId: string, identityId: string): Promise<void> {
  await call('/projects/{projectId}/remote-ai/{identityId}', () =>
    api.DELETE('/projects/{projectId}/remote-ai/{identityId}', {
      params: { path: { projectId, identityId } },
    }),
  )
}

export type RemoteAISandbox = Schemas['RemoteAISandbox']
export type RemoteAISandboxList = Schemas['RemoteAISandboxList']
export type RemoteAISandboxCreate = Schemas['RemoteAISandboxCreate']
export type RemoteAISandboxAction = 'start' | 'stop' | 'reconnect' | 'rebuild' | 'destroy'

export function listRemoteAISandboxes(projectId: string, identityId: string): Promise<RemoteAISandboxList> {
  return call('/projects/{projectId}/remote-ai/{identityId}/sandboxes', () =>
    api.GET('/projects/{projectId}/remote-ai/{identityId}/sandboxes', {
      params: { path: { projectId, identityId } },
    }),
  )
}

export function createRemoteAISandbox(
  projectId: string,
  identityId: string,
  input: RemoteAISandboxCreate,
): Promise<RemoteAISandbox> {
  return call('/projects/{projectId}/remote-ai/{identityId}/sandboxes', () =>
    api.POST('/projects/{projectId}/remote-ai/{identityId}/sandboxes', {
      params: { path: { projectId, identityId } },
      body: input,
    }),
  )
}

export function actOnRemoteAISandbox(
  projectId: string,
  identityId: string,
  sandboxId: string,
  action: RemoteAISandboxAction,
): Promise<RemoteAISandbox> {
  return call('/projects/{projectId}/remote-ai/{identityId}/sandboxes/{sandboxId}/{action}', () =>
    api.POST('/projects/{projectId}/remote-ai/{identityId}/sandboxes/{sandboxId}/{action}', {
      params: { path: { projectId, identityId, sandboxId, action } },
    }),
  )
}

/**
 * LOCAL_EXECUTOR_PROVIDER 是本机常驻规则执行器那一类身份的 provider 值。
 *
 * **它不是一个名字，是一个能力判据**：只有这一类身份跑着 luluexec，
 * 而 luluexec 只会往 README 与 docs 里加一行——它不写代码。
 * 权威那一份在 Go 里（`developmenttasks.LocalExecutorProvider`），
 * 开通、派活、建任务那道门三处都读它。
 *
 * **两边漂了会怎样，这里说清楚**：这一份只用来在下拉里挂一句提示。
 * 它对不上，后果是那句提示不出现——而**服务端那道门照样会拒**
 * （`development_task.assignee.cannot_execute`）。也就是说漂移会让
 * 这里退化成「没提示」，不会退化成「活派出去静默没人做」。
 * 这是刻意留的不对称：能拦住的那一半在服务端，这一半只是让他不必先撞一次。
 */
export const LOCAL_EXECUTOR_PROVIDER = 'npc-local'
